import {
  Box,
  Button,
  Text,
  Avatar,
  Spinner,
  useToast,
  Grid,
  GridItem,
  VStack,
  HStack,
  Divider,
  Flex,
  Input,
  Badge,
  Icon,
  InputGroup,
  InputLeftElement,
  useDisclosure,
  Select,
  Image,
  Container,
  Heading,
  IconButton,
  Tooltip,
  useColorModeValue,
  Card,
  CardBody,
  CardHeader,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchMotoristas } from "../apis/get-motoristas";
import { MdWarning, MdSearch, MdSortByAlpha, MdFilterList, MdClear, MdBusiness, MdPerson, MdSchedule, MdBusinessCenter } from "react-icons/md";
import { FaArrowRight, FaCalendarAlt, FaCalendarCheck, FaBuilding, FaIndustry, FaUsers, FaFilter, FaTimes } from "react-icons/fa";
import { FiRefreshCcw, FiTrendingUp, FiTrendingDown } from "react-icons/fi";
import SSLCertificateHelper from "../components/SSLCertificateHelper";

function SelecionarMotorista() {
  // Lista de motoristas que não devem ser exibidos
  const MOTORISTAS_EXCLUIDOS = [
    "ADIVALDO SILVA MAGALHAES",
    "ADRIANO DE OLIVEIRA ALBUQUERQUE",
    "ANDERSON MACIEL MARQUES",
    "EDSON RAMOS DE OLIVEIRA",
    "EMERSON ADORNO GOMES",
    "MARCOS ANTONIO GIMENEZ BRIZUENA",
    "MARCOS AURELIO PESSOA RODRIGUE",
    "MARIVAL MAIA DA SILVA",
    "ODAIR JOSE FERREIRA FRANCISCO",
    "VADO DA SILVA BENITES"
  ];

  const [motoristas, setMotoristas] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderAsc, setOrderAsc] = useState(true);
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);
  const [sslError, setSslError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState("todos");
  const [empresaFilter, setEmpresaFilter] = useState("todas");
  const [empresas, setEmpresas] = useState([]);
  const navigate = useNavigate();
  const toast = useToast();
  const sslHelperDisclosure = useDisclosure();
  const hasFetchedMotoristas = useRef(false);

  // Cores responsivas
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedTextColor = useColorModeValue("gray.600", "gray.400");

  // Breakpoints responsivos
  const isMobile = useBreakpointValue({ base: true, md: false });
  const gridCols = useBreakpointValue({ base: 1, md: 2, lg: 3, xl: 4 });

  useEffect(() => {
    sessionStorage.clear();
  }, []);

  // Função estável para abrir o modal SSL
  const openSSLHelper = useCallback(() => {
    sslHelperDisclosure.onOpen();
  }, [sslHelperDisclosure]);

  // Função estável para mostrar toast de erro
  const showErrorToast = useCallback((message) => {
    toast({
      title: "Erro ao carregar motoristas",
      description: message,
      status: "error",
      duration: 5000,
      isClosable: true,
    });
  }, [toast]);

  useEffect(() => {
    // Evitar chamadas múltiplas da API
    if (hasFetchedMotoristas.current) {
      return;
    }

    const loadMotoristas = async () => {
      try {
        hasFetchedMotoristas.current = true;
        const data = await fetchMotoristas();
        // Filtrar motoristas excluídos (comparação case-insensitive)
        const filtered = data.filter(
          (mot) => !MOTORISTAS_EXCLUIDOS.some(
            (excluido) => mot.nome.toUpperCase().trim() === excluido.toUpperCase().trim()
          )
        );
        const sorted = filtered.sort((a, b) => a.nome.localeCompare(b.nome));
        setMotoristas(sorted);
        setFiltered(sorted);
        
        // Definir empresas padrão para o filtro
        setEmpresas(["IMPACTO", "CENZE", "SEM EMPRESA"]);
        
        setError(null);
        setSslError(null);
      } catch (err) {
        console.log("🔍 DEBUG: Erro capturado:", err);
        console.log("🔍 DEBUG: Tipo do erro:", err.type);
        console.log("🔍 DEBUG: Mensagem do erro:", err.message);
        console.log("🔍 DEBUG: Código do erro:", err.code);
        console.log("🔍 DEBUG: Propriedades do erro:", Object.keys(err));
        
        // Tratamento específico para erro 503 (Service Unavailable)
        if (err.message?.includes('Serviço temporariamente indisponível') || 
            err.message?.includes('Erro de Licença')) {
          console.log("🚨 Erro 503 detectado na interface!");
          setError(err.message);
          showErrorToast(err.message);
          return;
        }
        
        // Tratamento específico para erros SSL
        if (err.type === 'SSL_ERROR' || 
            err.message?.includes('SSL') || 
            err.message?.includes('certificate') ||
            err.message?.includes('CERT')) {
          console.log("🚨 SSL detectado!");
          setSslError(err);
          openSSLHelper();
        } else {
          console.log("❌ Não é SSL, mostrando erro genérico");
          setError(err.message);
          showErrorToast(err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    loadMotoristas();
  }, [openSSLHelper, showErrorToast]);

  const applyFilters = useCallback(() => {
    // Primeiro, garantir que motoristas excluídos não sejam exibidos (comparação case-insensitive)
    let filtered = motoristas.filter(
      (mot) => !MOTORISTAS_EXCLUIDOS.some(
        (excluido) => mot.nome.toUpperCase().trim() === excluido.toUpperCase().trim()
      )
    );

    // Filtro por nome
    if (search) {
      filtered = filtered.filter((mot) =>
        mot.nome.toLowerCase().includes(search.toLowerCase())
      );
    }

    // Filtro por status
    if (statusFilter !== "todos") {
      const dias = (motorista) => {
        // Verificar se a data é válida antes de calcular
        if (!motorista.ultimo_apto || motorista.ultimo_apto === "--" || isNaN(new Date(motorista.ultimo_apto).getTime())) {
          return Infinity; // Motoristas sem data válida são considerados desatualizados
        }
        return Math.floor((new Date() - new Date(motorista.ultimo_apto)) / (1000 * 60 * 60 * 24));
      };
      
      if (statusFilter === "ativo") {
        filtered = filtered.filter(mot => dias(mot) <= 7);
      } else if (statusFilter === "desativo") {
        filtered = filtered.filter(mot => dias(mot) > 7);
      }
    }

    // Filtro por empresa
    if (empresaFilter && empresaFilter !== "todas") {
      filtered = filtered.filter((mot) => 
        mot.empresa === empresaFilter
      );
    }

    setFiltered(filtered);
  }, [motoristas, search, statusFilter, empresaFilter]);

  const handleSearch = (term) => {
    setSearch(term);
  };

  const clearAllFilters = () => {
    setSearch("");
    setStatusFilter("todos");
    setEmpresaFilter("todas");
  };

  const toggleOrder = () => {
    const ordered = [...filtered].sort((a, b) =>
      orderAsc ? b.nome.localeCompare(a.nome) : a.nome.localeCompare(b.nome)
    );
    setFiltered(ordered);
    setOrderAsc(!orderAsc);
  };

  // Aplicar filtros sempre que os filtros mudarem
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const getEmpresaConfig = (empresa) => {
    switch (empresa) {
      case "IMPACTO":
        return {
          color: "blue",
          borderColor: "blue.500",
          icon: FaIndustry,
          bgColor: "blue.50",
          cardBg: "linear-gradient(135deg, blue.25 0%, blue.50 100%)",
          logo: "/impacto_transportes_ltda_logo.jpg",
          gradient: "linear-gradient(135deg, #EBF8FF 0%, #BEE3F8 100%)"
        };
      case "CENZE":
        return {
          color: "purple",
          borderColor: "purple.500",
          icon: MdBusiness,
          bgColor: "purple.50",
          cardBg: "linear-gradient(135deg, purple.25 0%, purple.50 100%)",
          logo: "/trr_cenze_comrcio_de_combustveis_e_derivados_logo.jpg",
          gradient: "linear-gradient(135deg, #FAF5FF 0%, #E9D8FD 100%)"
        };
      case "SEM EMPRESA":
      default:
        return {
          color: "gray",
          borderColor: "gray.500",
          icon: FaBuilding,
          bgColor: "gray.50",
          cardBg: "linear-gradient(135deg, gray.25 0%, gray.50 100%)",
          logo: null,
          gradient: "linear-gradient(135deg, #F7FAFC 0%, #EDF2F7 100%)"
        };
    }
  };

  const renderBadgeStatus = (motorista) => {
    // Verificar se a data é válida antes de calcular
    if (!motorista.ultimo_apto || motorista.ultimo_apto === "--" || isNaN(new Date(motorista.ultimo_apto).getTime())) {
      return (
        <Badge 
          colorScheme="gray" 
          variant="solid" 
          borderRadius="full" 
          px={3} 
          py={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
          fontSize="xs"
          fontWeight="medium"
          lineHeight="1"
        >
          ⚠ Sem data
        </Badge>
      );
    }

    const dias = Math.floor(
      (new Date() - new Date(motorista.ultimo_apto)) / (1000 * 60 * 60 * 24)
    );

    if (dias <= 2) return (
      <Badge 
        colorScheme="green" 
        variant="solid" 
        borderRadius="full" 
        px={3} 
        py={1}
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontSize="xs"
        fontWeight="medium"
        lineHeight="1"
      >
        ✓ Atualizado
      </Badge>
    );
    if (dias <= 7) return (
      <Badge 
        colorScheme="yellow" 
        variant="solid" 
        borderRadius="full" 
        px={3} 
        py={1}
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontSize="xs"
        fontWeight="medium"
        lineHeight="1"
      >
        ⚠ Recente
      </Badge>
    );
    return (
      <Badge 
        colorScheme="red" 
        variant="solid" 
        borderRadius="full" 
        px={3} 
        py={1}
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontSize="xs"
        fontWeight="medium"
        lineHeight="1"
      >
        ✗ Desatualizado
      </Badge>
    );
  };

  // Estatísticas
  const getStats = () => {
    const total = motoristas.length;
    const ativos = motoristas.filter(mot => {
      // Verificar se a data é válida antes de calcular
      if (!mot.ultimo_apto || mot.ultimo_apto === "--" || isNaN(new Date(mot.ultimo_apto).getTime())) {
        return false; // Motoristas sem data válida não são considerados ativos
      }
      const dias = Math.floor((new Date() - new Date(mot.ultimo_apto)) / (1000 * 60 * 60 * 24));
      return dias <= 7;
    }).length;
    const desatualizados = total - ativos;
    
    return { total, ativos, desatualizados };
  };

  const stats = getStats();

  if (loading) {
    return (
      <Flex 
        justify="center" 
        align="center" 
        height="100vh" 
        bg={bgColor}
        direction="column"
        gap={6}
      >
        <Box
          bg={cardBg}
          p={8}
          borderRadius="2xl"
          boxShadow="xl"
          border="1px solid"
          borderColor={borderColor}
          textAlign="center"
        >
          <Spinner size="xl" color="red.500" thickness="4px" mb={4} />
          <Text fontSize="lg" color={textColor} fontWeight="medium">
            Carregando motoristas...
          </Text>
          <Text fontSize="sm" color={mutedTextColor} mt={2}>
            Aguarde enquanto buscamos as informações
          </Text>
        </Box>
      </Flex>
    );
  }

  if (error) {
    const isErroLicenca =
      error?.toLowerCase().includes("licenca") ||
      error.includes("License Server");
    
    const isErro503 =
      error?.includes("Serviço temporariamente indisponível") ||
      error?.includes("Service Unavailable");

    return (
      <Box
        minH="100vh"
        bg={bgColor}
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={6}
      >
        <Box
          maxW="500px"
          w="100%"
          bg={cardBg}
          borderRadius="2xl"
          boxShadow="2xl"
          border="1px solid"
          borderColor="red.100"
          overflow="hidden"
        >
          {/* Header com ícone */}
          <Box
            bg="red.500"
            p={6}
            textAlign="center"
            position="relative"
          >
            <Box
              bg="white"
              borderRadius="full"
              w="80px"
              h="80px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              mx="auto"
              mb={4}
              boxShadow="lg"
            >
              <Icon as={MdWarning} boxSize={10} color="red.500" />
            </Box>
            <Text fontSize="2xl" fontWeight="bold" color="white" mb={2}>
              {isErroLicenca
                ? "Licença Indisponível"
                : isErro503
                ? "Serviço Indisponível"
                : "Erro de Conexão"}
            </Text>
            <Text fontSize="md" color="red.100" opacity={0.9}>
              {isErroLicenca
                ? "Servidor de licenças temporariamente indisponível"
                : isErro503
                ? "Servidor sobrecarregado ou em manutenção"
                : "Não foi possível conectar ao servidor"}
            </Text>
          </Box>

          {/* Conteúdo */}
          <Box p={8}>
            <Text fontSize="lg" color={textColor} textAlign="center" mb={6} lineHeight="tall">
              {isErroLicenca
                ? "O servidor de licenças não possui licenças disponíveis no momento. Isso pode acontecer devido ao alto volume de acessos ou manutenção do sistema."
                : isErro503
                ? "O servidor está temporariamente indisponível. Isso pode acontecer devido ao alto volume de acessos, manutenção programada ou problemas técnicos. Tente novamente em alguns instantes."
                : "Não foi possível obter a lista de motoristas no momento. Verifique sua conexão com a internet ou tente novamente em alguns instantes."}
            </Text>

            {/* Botões */}
            <VStack spacing={4} mt={8}>
              <Button
                colorScheme="red"
                size="lg"
                w="100%"
                onClick={() => window.location.reload()}
                leftIcon={<FiRefreshCcw />}
                borderRadius="xl"
                fontWeight="bold"
                fontSize="md"
                py={6}
                _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                transition="all 0.2s"
              >
                Tentar Novamente
              </Button>
            </VStack>

            {/* Informação adicional */}
            <Box
              mt={6}
              p={4}
              bg="blue.50"
              borderRadius="lg"
              border="1px solid"
              borderColor="blue.200"
            >
              <Text fontSize="sm" color="blue.700" textAlign="center">
                💡 <strong>Dica:</strong> Se o problema persistir, atualize a página ou tente novamente em alguns instantes.
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box bg={bgColor} minH="100vh" py={6}>
      <Container maxW="7xl" px={4}>
                 {/* Header com navegação e título integrado */}
         <Box 
           bg={cardBg} 
           border="1px solid" 
           borderColor={borderColor} 
           borderRadius="3xl" 
           boxShadow="xl"
           p={8}
           mb={8}
           position="relative"
           overflow="hidden"
         >
           {/* Background decorativo */}
           <Box
             position="absolute"
             top={0}
             right={0}
             w="300px"
             h="300px"
             bg="linear-gradient(135deg, #E31E2C 0%, #C51B26 100%)"
             borderRadius="full"
             transform="translate(100px, -100px)"
             opacity={0.1}
           />
           
           {/* Conteúdo do header */}
           <Box position="relative" zIndex={1}>
             {/* Título principal com design melhorado */}
             <Box textAlign="center">
               {/* Ícone decorativo */}
               <Box
                 bg="linear-gradient(135deg, #E31E2C 0%, #C51B26 100%)"
                 w="80px"
                 h="80px"
                 borderRadius="full"
                 display="flex"
                 alignItems="center"
                 justifyContent="center"
                 mx="auto"
                 mb={6}
                 boxShadow="2xl"
                 position="relative"
               >
                 <Icon 
                   as={FaUsers} 
                   boxSize={10} 
                   color="white" 
                 />
                 {/* Brilho no ícone */}
                 <Box
                   position="absolute"
                   top="10%"
                   left="10%"
                   w="20%"
                   h="20%"
                   bg="white"
                   borderRadius="full"
                   opacity={0.3}
                 />
               </Box>
               
               {/* Título principal */}
               <Heading 
                 as="h1" 
                 size="3xl" 
                 color="#E31E2C" 
                 mb={4}
                 fontWeight="black"
                 letterSpacing="tight"
                 textShadow="0 2px 4px rgba(0,0,0,0.1)"
               >
                 Selecione um Motorista
               </Heading>
               
               {/* Subtítulo */}
               <Text 
                 fontSize="xl" 
                 color={mutedTextColor} 
                 maxW="3xl" 
                 mx="auto"
                 lineHeight="tall"
                 fontWeight="medium"
               >
                 Utilize a busca inteligente e filtros avançados para encontrar rapidamente o motorista desejado
               </Text>
               
               {/* Linha decorativa */}
               <Box
                 w="100px"
                 h="4px"
                 bg="linear-gradient(90deg, #E31E2C 0%, #C51B26 100%)"
                 borderRadius="full"
                 mx="auto"
                 mt={6}
                 boxShadow="md"
               />
             </Box>
           </Box>
         </Box>

        {/* Estatísticas */}
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={6} mb={8}>
          <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" boxShadow="lg">
            <CardBody textAlign="center">
              <Stat>
                <StatLabel color={mutedTextColor} fontSize="sm" fontWeight="medium">Total de Motoristas</StatLabel>
                <StatNumber color={textColor} fontSize="3xl" fontWeight="bold">{stats.total}</StatNumber>
                <StatHelpText color={mutedTextColor}>
                  <Icon as={FaUsers} mr={2} />
                  Motoristas cadastrados
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" boxShadow="lg">
            <CardBody textAlign="center">
              <Stat>
                <StatLabel color={mutedTextColor} fontSize="sm" fontWeight="medium">Motoristas Ativos</StatLabel>
                <StatNumber color="green.500" fontSize="3xl" fontWeight="bold">{stats.ativos}</StatNumber>
                <StatHelpText color="green.500">
                  <Icon as={FiTrendingUp} mr={2} />
                  Atualizados nos últimos 7 dias
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          
          <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" boxShadow="lg">
            <CardBody textAlign="center">
              <Stat>
                <StatLabel color={mutedTextColor} fontSize="sm" fontWeight="medium">Desatualizados</StatLabel>
                <StatNumber color="red.500" fontSize="3xl" fontWeight="bold">{stats.desatualizados}</StatNumber>
                <StatHelpText color="red.500">
                  <Icon as={FiTrendingDown} mr={2} />
                  Sem atualização recente
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
        
        {/* Barra de busca e filtros */}
        <Card 
          bg={cardBg} 
          border="1px solid" 
          borderColor={borderColor} 
          borderRadius="2xl" 
          boxShadow="xl"
          mb={8}
          overflow="hidden"
        >
          <CardHeader bg="gray.50" borderBottom="1px solid" borderColor={borderColor}>
            <Flex align="center" gap={3}>
              <Icon as={FaFilter} color="red.500" boxSize={5} />
              <Text fontSize="lg" fontWeight="bold" color={textColor}>
                Busca e Filtros
              </Text>
            </Flex>
          </CardHeader>
          
          <CardBody p={6}>
            {/* Barra de busca principal */}
            <Flex
              justify="center"
              align="center"
              gap={4}
              flexWrap="wrap"
              mb={showFilters ? 6 : 0}
            >
              <InputGroup maxW="400px">
                <InputLeftElement pointerEvents="none">
                  <MdSearch color="gray.400" size={20} />
                </InputLeftElement>
                <Input
                  placeholder="Digite o nome do motorista..."
                  bg="white"
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  size="lg"
                  borderRadius="xl"
                  border="2px solid"
                  borderColor={borderColor}
                  _focus={{ borderColor: "red.500", boxShadow: "0 0 0 1px #E31E2C" }}
                  aria-label="Buscar motorista por nome"
                />
              </InputGroup>
              
              <Button
                size="lg"
                leftIcon={<MdSortByAlpha />}
                colorScheme="blue"
                variant="outline"
                onClick={toggleOrder}
                aria-label="Ordenar motoristas"
                title={orderAsc ? "Ordenar de A a Z" : "Ordenar de Z a A"}
                borderRadius="xl"
                px={6}
                _hover={{ transform: "translateY(-2px)", boxShadow: "md" }}
                transition="all 0.2s"
              >
                {orderAsc ? "A–Z" : "Z–A"}
              </Button>
              
              <Button
                size="lg"
                leftIcon={<FaFilter />}
                colorScheme={showFilters ? "orange" : "gray"}
                variant={showFilters ? "solid" : "outline"}
                onClick={() => setShowFilters(!showFilters)}
                aria-label="Mostrar/ocultar filtros"
                borderRadius="xl"
                px={6}
                _hover={{ transform: "translateY(-2px)", boxShadow: "md" }}
                transition="all 0.2s"
              >
                {showFilters ? "Ocultar Filtros" : "Mostrar Filtros"}
              </Button>
              
              {(search || statusFilter !== "todos" || empresaFilter !== "todas") && (
                <Button
                  size="lg"
                  leftIcon={<FaTimes />}
                  colorScheme="red"
                  variant="outline"
                  onClick={clearAllFilters}
                  aria-label="Limpar todos os filtros"
                  borderRadius="xl"
                  px={6}
                  _hover={{ transform: "translateY(-2px)", boxShadow: "md" }}
                  transition="all 0.2s"
                >
                  Limpar
                </Button>
              )}
            </Flex>
            
            {/* Filtros avançados */}
            {showFilters && (
              <Box
                borderTop="2px solid"
                borderColor={borderColor}
                pt={6}
              >
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
                  {/* Filtro por Status */}
                  <Box>
                    <Text fontSize="md" fontWeight="bold" color={textColor} mb={3} display="flex" align="center" gap={2}>
                      <Icon as={MdSchedule} color="red.500" />
                      Status do Motorista
                    </Text>
                    <Flex gap={3} flexWrap="wrap">
                      <Button
                        size="md"
                        colorScheme={statusFilter === "todos" ? "blue" : "gray"}
                        variant={statusFilter === "todos" ? "solid" : "outline"}
                        onClick={() => setStatusFilter("todos")}
                        borderRadius="lg"
                        fontWeight="medium"
                      >
                        Todos
                      </Button>
                      <Button
                        size="md"
                        colorScheme={statusFilter === "ativo" ? "green" : "gray"}
                        variant={statusFilter === "ativo" ? "solid" : "outline"}
                        onClick={() => setStatusFilter("ativo")}
                        borderRadius="lg"
                        fontWeight="medium"
                      >
                        Ativos
                      </Button>
                      <Button
                        size="md"
                        colorScheme={statusFilter === "desativo" ? "red" : "gray"}
                        variant={statusFilter === "desativo" ? "solid" : "outline"}
                        onClick={() => setStatusFilter("desativo")}
                        borderRadius="lg"
                        fontWeight="medium"
                      >
                        Desatualizados
                      </Button>
                    </Flex>
                  </Box>
                  
                  {/* Filtro por Empresa */}
                  <Box>
                    <Text fontSize="md" fontWeight="bold" color={textColor} mb={3} display="flex" align="center" gap={2}>
                      <Icon as={MdBusinessCenter} color="red.500" />
                      Empresa
                    </Text>
                    <Select
                      value={empresaFilter}
                      onChange={(e) => setEmpresaFilter(e.target.value)}
                      size="md"
                      placeholder="Selecione uma empresa"
                      borderRadius="lg"
                      border="2px solid"
                      borderColor={borderColor}
                      _focus={{ borderColor: "red.500", boxShadow: "0 0 0 1px #E31E2C" }}
                    >
                      <option value="todas">Todas as Empresas</option>
                      <option value="IMPACTO">IMPACTO</option>
                      <option value="CENZE">CENZE</option>
                      <option value="SEM EMPRESA">SEM EMPRESA</option>
                    </Select>
                  </Box>
                </SimpleGrid>
              </Box>
            )}
          </CardBody>
        </Card>
        
        {/* Indicador de filtros ativos */}
        {(search || statusFilter !== "todos" || (empresaFilter && empresaFilter !== "todas")) && (
          <Card 
            bg="blue.50" 
            border="1px solid" 
            borderColor="blue.200" 
            borderRadius="xl" 
            mb={8}
            boxShadow="md"
          >
            <CardBody p={4}>
              <Flex align="center" justify="space-between" flexWrap="wrap" gap={3}>
                <HStack spacing={3} flexWrap="wrap">
                  <Text fontSize="md" fontWeight="bold" color="blue.700">
                    Filtros ativos:
                  </Text>
                  {search && (
                    <Badge colorScheme="blue" variant="solid" borderRadius="full" px={3} py={1}>
                      Nome: "{search}"
                    </Badge>
                  )}
                  {statusFilter !== "todos" && (
                    <Badge 
                      colorScheme={statusFilter === "ativo" ? "green" : "red"} 
                      variant="solid" 
                      borderRadius="full" 
                      px={3} 
                      py={1}
                    >
                      Status: {statusFilter === "ativo" ? "Ativos" : "Desatualizados"}
                    </Badge>
                  )}
                  {empresaFilter && empresaFilter !== "todas" && (
                    <Badge colorScheme="purple" variant="solid" borderRadius="full" px={3} py={1}>
                      Empresa: {empresaFilter}
                    </Badge>
                  )}
                </HStack>
                <Text fontSize="md" fontWeight="bold" color="blue.600">
                  {filtered.length} motorista{filtered.length !== 1 ? 's' : ''} encontrado{filtered.length !== 1 ? 's' : ''}
                </Text>
              </Flex>
            </CardBody>
          </Card>
        )}
        
        {/* Lista de motoristas ou mensagem de nenhum resultado */}
        {filtered.length === 0 ? (
          <Card 
            bg={cardBg} 
            border="1px solid" 
            borderColor="yellow.200" 
            borderRadius="2xl" 
            boxShadow="xl"
            overflow="hidden"
          >
            <CardHeader bg="yellow.500" textAlign="center">
              <Box
                bg="white"
                borderRadius="full"
                w="80px"
                h="80px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                mx="auto"
                mb={4}
                boxShadow="lg"
              >
                <Icon as={MdSearch} boxSize={10} color="yellow.500" />
              </Box>
              <Text fontSize="2xl" fontWeight="bold" color="white" mb={2}>
                {search || statusFilter !== "todos" || (empresaFilter && empresaFilter !== "todas") ? "Nenhum Resultado" : "Lista Vazia"}
              </Text>
              <Text fontSize="md" color="yellow.100" opacity={0.9}>
                {search || statusFilter !== "todos" || (empresaFilter && empresaFilter !== "todas")
                  ? "Sua busca não retornou resultados"
                  : "Nenhum motorista disponível no momento"
                }
              </Text>
            </CardHeader>

            <CardBody p={8}>
              <Text fontSize="lg" color={textColor} textAlign="center" mb={6} lineHeight="tall">
                {search || statusFilter !== "todos" || (empresaFilter && empresaFilter !== "todas")
                  ? `Não encontramos motoristas com os filtros aplicados. Tente ajustar os filtros ou limpe-os para ver todos os motoristas.`
                  : "Não há motoristas disponíveis no momento. Isso pode acontecer devido a problemas de conexão com o servidor."
                }
              </Text>

              {/* Botões */}
              <VStack spacing={4} mt={8}>
                {(search || statusFilter !== "todos" || (empresaFilter && empresaFilter !== "todas")) && (
                  <Button
                    colorScheme="yellow"
                    size="lg"
                    w="100%"
                    onClick={clearAllFilters}
                    leftIcon={<FaTimes />}
                    borderRadius="xl"
                    fontWeight="bold"
                    fontSize="md"
                    py={6}
                    _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
                    transition="all 0.2s"
                  >
                    Limpar Todos os Filtros
                  </Button>
                )}
              </VStack>

              {/* Informação adicional */}
              <Box
                mt={6}
                p={4}
                bg="blue.50"
                borderRadius="lg"
                border="1px solid"
                borderColor="blue.200"
              >
                <Text fontSize="sm" color="blue.700" textAlign="center">
                  💡 <strong>Dica:</strong> Se o problema persistir, atualize a página ou tente novamente em alguns instantes.
                </Text>
              </Box>
            </CardBody>
          </Card>
        ) : (
                     /* Grid de motoristas */
           <SimpleGrid columns={gridCols} gap={6}>
             {filtered.map((motorista) => {
               const empresaConfig = getEmpresaConfig(motorista.empresa);
               const isMotoristaTeste = motorista.nome?.toUpperCase().includes('MOTORISTA TESTE');
               
               return (
                 <Card
                   key={motorista.codigo}
                   bg={isMotoristaTeste ? "red.50" : cardBg}
                   border="2px solid"
                   borderColor={isMotoristaTeste ? "red.500" : empresaConfig.borderColor}
                   borderRadius="2xl"
                   boxShadow="lg"
                   overflow="hidden"
                   _hover={{ 
                     boxShadow: "2xl", 
                     transform: "translateY(-4px)",
                     borderColor: isMotoristaTeste ? "red.600" : empresaConfig.borderColor
                   }}
                   transition="all 0.3s ease-in-out"
                   position="relative"
                   height="100%"
                   display="flex"
                   flexDirection="column"
                 >
                   {/* Header com gradiente */}
                   <Box
                     bg={isMotoristaTeste ? "linear-gradient(135deg, #FED7D7 0%, #FEB2B2 100%)" : empresaConfig.gradient}
                     p={4}
                     borderBottom="1px solid"
                     borderColor={borderColor}
                     flexShrink={0}
                   >
                     <Flex align="center" gap={3}>
                       <Box
                         w="50px"
                         h="50px"
                         borderRadius="full"
                         border="2px solid"
                         borderColor={isMotoristaTeste ? "red.500" : empresaConfig.borderColor}
                         bg="white"
                         overflow="hidden"
                         display="flex"
                         alignItems="center"
                         justifyContent="center"
                         boxShadow="md"
                         flexShrink={0}
                       >
                         {empresaConfig.logo ? (
                           <Image
                             src={empresaConfig.logo}
                             alt={`Logo ${motorista.empresa}`}
                             w="80%"
                             h="80%"
                             objectFit="contain"
                             borderRadius="full"
                           />
                         ) : (
                           <Icon 
                             as={empresaConfig.icon} 
                             color={isMotoristaTeste ? "red.500" : empresaConfig.borderColor} 
                             boxSize={6} 
                           />
                         )}
                       </Box>
                       <Box flex={1} minW={0}>
                         <Text fontSize="lg" fontWeight="bold" color={textColor} noOfLines={2}>
                           {motorista.nome}
                         </Text>
                         <Text fontSize="xs" color={mutedTextColor} noOfLines={1}>
                           Código: {motorista.codigo}
                         </Text>
                       </Box>
                     </Flex>
                   </Box>
                   
                   <CardBody p={5} flex={1} display="flex" flexDirection="column">
                     {/* Badges de status */}
                     <Box mb={4}>
                       <Flex gap={2} flexWrap="wrap">
                         <Badge
                           colorScheme={empresaConfig.color}
                           variant="solid"
                           fontSize="xs"
                           px={3}
                           py={1}
                           borderRadius="full"
                           fontWeight="medium"
                         >
                           {motorista.empresa}
                         </Badge>
                         {isMotoristaTeste && (
                           <Badge
                             colorScheme="red"
                             variant="solid"
                             fontSize="xs"
                             px={3}
                             py={1}
                             borderRadius="full"
                             fontWeight="bold"
                             textTransform="uppercase"
                           >
                             🧪 Motorista Teste
                           </Badge>
                         )}
                         {renderBadgeStatus(motorista)}
                       </Flex>
                     </Box>
                     
                     {/* Informações do motorista */}
                     <Box mb={4} flex={1}>
                       <VStack align="start" spacing={3}>
                                                   <HStack spacing={3} w="100%">
                            <Icon as={FaCalendarAlt} color="#E31E2C" boxSize={4} flexShrink={0} />
                            <Text fontSize="sm" color={mutedTextColor} flex={1}>
                              Primeiro Apto: <Text as="span" fontWeight="bold" color={textColor}>
                                {motorista.primeiro_apto && motorista.primeiro_apto !== "--" && !isNaN(new Date(motorista.primeiro_apto).getTime()) 
                                  ? new Date(motorista.primeiro_apto).toLocaleDateString() 
                                  : "Sem data informada"}
                              </Text>
                            </Text>
                          </HStack>
                         <HStack spacing={3} w="100%">
                           <Icon as={FaCalendarCheck} color="#E31E2C" boxSize={4} flexShrink={0} />
                           <Text fontSize="sm" color={mutedTextColor} flex={1}>
                             Último Apto: <Text as="span" fontWeight="bold" color={textColor}>
                               {new Date(motorista.ultimo_apto).toLocaleDateString()}
                             </Text>
                           </Text>
                         </HStack>
                         <HStack spacing={3} w="100%">
                           <Icon 
                             as={empresaConfig.icon} 
                             color={empresaConfig.borderColor} 
                             boxSize={4} 
                             flexShrink={0}
                           />
                           <Text fontSize="sm" color={mutedTextColor} flex={1}>
                             Empresa: <Text as="span" fontWeight="bold" color={empresaConfig.borderColor}>
                               {motorista.empresa || "Não informada"}
                             </Text>
                           </Text>
                         </HStack>
                       </VStack>
                     </Box>
                     
                                           {/* Botão de seleção - sempre na parte inferior */}
                      <Box mt="auto" pt={2}>
                        <Button
                          color="white"
                          bg="gray.500"
                          leftIcon={<FaArrowRight />}
                          width="100%"
                          aria-label={`Selecionar ${motorista.nome}`}
                          onClick={() =>
                            navigate(`/${motorista.codigo}/escolher-acao`, {
                              state: { motorista },
                            })
                          }
                          _hover={{ 
                            bg: "#C51B26",
                            transform: "translateY(-2px)",
                            boxShadow: "lg"
                          }}
                          fontWeight="bold"
                          fontSize="md"
                          borderRadius="xl"
                          boxShadow="md"
                          transition="all 0.2s"
                          py={6}
                        >
                          Selecionar Motorista
                        </Button>
                      </Box>
                   </CardBody>
                 </Card>
               );
             })}
           </SimpleGrid>
        )}
      </Container>

      {/* SSL Certificate Helper Modal */}
      <SSLCertificateHelper
        isOpen={sslHelperDisclosure.isOpen}
        onClose={sslHelperDisclosure.onClose}
        error={sslError}
        baseUrl={sslError?.baseUrl}
      />
    </Box>
  );
}

export default SelecionarMotorista;
